<template>
  <div class="d-flex flex-column flex-root">
    <div
      class="d-flex flex-row-fluid flex-column align-items-center justify-content-center p-10 p-sm-30"
    >
      <!-- STATE: checking (mobile) -->
      <div v-if="isMobile && hasApp === null" class="text-center">
        <h1 class="font-size-h2 font-weight-bold text-dark-75 mb-5">
          Uygulama açılıyor…
        </h1>
        <div class="spinner spinner-primary spinner-lg spinner-shorten"></div>
        <p class="font-size-md text-muted mt-4">
          Lütfen bekleyin, Robosay uygulamanız açılacak.
        </p>
      </div>

      <!-- STATE: not installed / failed (mobile) -->
      <div v-else-if="isMobile && hasApp === false" class="text-center">
        <h1 class="font-size-h2 font-weight-bold text-dark-75 mb-5">
          Robosay uygulaması bulunamadı
        </h1>
        <p class="font-size-md text-muted mb-6">
          Uygulamanız yüklü değilse mağazadan indirebilir veya tekrar denemek
          için aşağıya tıklayabilirsiniz.
        </p>
        <div
          class="d-flex flex-column flex-sm-row align-items-center justify-content-center gap-3 mb-6"
        >
          <a
            :href="deepLink"
            @click.prevent="openApp(false)"
            class="btn btn-primary btn-lg font-weight-bold mb-3 mb-sm-0 mr-sm-3"
            rel="noopener"
          >
            Uygulamada Aç
          </a>
        </div>

        <!-- Store badges -->
        <div class="mt-4">
          <div class="d-flex align-items-center justify-content-center">
            <a
              href="https://apps.apple.com/tr/app/robosay/id6446150573"
              target="_blank"
              rel="noopener"
              class="mx-2"
            >
              <img
                src="/media/logos/appstore.svg"
                alt="Robosay iOS"
                style="height: 48px"
              />
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=tr.com.robosay"
              target="_blank"
              rel="noopener"
              class="mx-2"
            >
              <img
                src="/media/logos/googleplay.svg"
                alt="Robosay Android"
                style="height: 48px"
              />
            </a>
          </div>
        </div>
      </div>

      <!-- STATE: desktop fallback text (rarely visible because we redirect) -->
      <div v-else class="text-center">
        <h1 class="font-size-h2 font-weight-bold text-dark-75 mb-5">
          Yönlendiriliyorsunuz…
        </h1>
        <p class="font-size-md text-muted">
          Eğer sayfa otomatik yönlenmezse,
          <a :href="storeLink" class="font-weight-bold">buraya tıklayın</a>.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import ApiService from "@/core/services/api.service";

export default {
  name: "Shortened-Links",
  data() {
    return {
      hasApp: null,
      platform: "web",
      deepLink: "",
      detectionTimer: null,
      detectionCleanup: null,
      fallbackTimer: null,
      apiBase: (process.env.VUE_APP_API_BASE_URL || "").replace(/\/+$/, ""),
      code: "",
      payload: null,
      fallbackUrl: "",
    };
  },
  computed: {
    isMobile() {
      return this.platform === "ios" || this.platform === "android";
    },
    callToActionText() {
      if (this.hasApp === true) {
        return "Uygulamada Aç";
      }
      if (this.hasApp === false) {
        return "Uygulamayı Yükle";
      }
      return "Uygulama Kontrol Ediliyor...";
    },
    storeLink() {
      if (this.platform === "ios") {
        return "https://apps.apple.com/tr/app/robosay/id6446150573";
      }
      if (this.platform === "android") {
        return "https://play.google.com/store/apps/details?id=tr.com.robosay&hl=tr";
      }
      return "https://robosay.com.tr";
    },
  },
  mounted() {
    this.platform = this.detectPlatform();
    this.code = this.getRelevantTokenFromShortenedLinkId(
      this.$route && this.$route.params && this.$route.params.id
        ? this.$route.params.id
        : ""
    );
    this.deepLink = this.buildDeepLink();

    if (!this.code) {
      this.hasApp = false;
      return;
    }

    // event: clicked
    this.postShortlinkEvent(this.code, "clicked");

    // Resolve payload then proceed
    this.resolveShortlink(this.code)
      .then((data) => {
        if (data?.status !== "ok" || !data?.payload) throw new Error("gone");

        if (data.resourceType === "payment_service_fee_invoice") {
          const targetUrl =
            typeof data.targetUrl === "string" ? data.targetUrl.trim() : "";

          if (!targetUrl) throw new Error("gone");

          window.location.replace(targetUrl);
          return;
        }

        this.payload = data.payload;
        this.fallbackUrl = this.buildFallbackUrlFromPayload(this.payload);

        if (this.isMobile) {
          // attempt app detection/open
          this.checkAppInstallation();
          // Also try to open immediately; fallback in checkAppInstallation handles store
          this.openApp(true);
        } else {
          this.hasApp = false;
        }
      })
      .catch(() => {
        this.hasApp = false;
      });
  },
  beforeDestroy() {
    this.teardownDetection();
    if (this.fallbackTimer) {
      clearTimeout(this.fallbackTimer);
    }
  },
  methods: {
    getRelevantTokenFromShortenedLinkId(shortenedId) {
      return shortenedId || "";
    },
    detectPlatform() {
      const nav = typeof navigator === "undefined" ? null : navigator;
      if (!nav) {
        return "web";
      }
      const userAgent = (nav.userAgent || "").toLowerCase();
      const vendor = (nav.vendor || "").toLowerCase();
      const combined = `${userAgent} ${vendor}`;
      if (combined.includes("android")) {
        return "android";
      }
      const hasMsStream =
        typeof window !== "undefined" && window.MSStream ? true : false;
      if (/ipad|iphone|ipod/.test(combined) && !hasMsStream) {
        return "ios";
      }
      return "web";
    },
    buildDeepLink() {
      const code = this.getRelevantTokenFromShortenedLinkId(
        this.$route && this.$route.params && this.$route.params.id
          ? this.$route.params.id
          : ""
      );
      if (!code) return "robosay://";
      return `robosay://s/${encodeURIComponent(
        code
      )}/?shortLinkCode=${encodeURIComponent(code)}`;
    },
    async resolveShortlink(code) {
      try {
        const { data } = await ApiService.get(
          "/shortlinks",
          `${encodeURIComponent(code)}/resolve`
        );
        return data;
      } catch (err) {
        const status = err?.response?.status;
        if (status === 410 || status === 404) {
          return { status: "gone" };
        }
        throw err;
      }
    },
    buildFallbackUrlFromPayload(payload) {
      const base = window.location.origin.replace(/\/+$/, "");
      const params = new URLSearchParams();
      if (payload) {
        if (payload.clusterId != null)
          params.set("clusterId", String(payload.clusterId));
        if (payload.residentId != null)
          params.set("residentId", String(payload.residentId));
        if (payload.houseUserId != null)
          params.set("houseUserId", String(payload.houseUserId));
      }
      return `${base}/pay${params.toString() ? `?${params}` : ""}`;
    },
    checkAppInstallation() {
      this.teardownDetection();
      if (
        !this.isMobile ||
        typeof window === "undefined" ||
        typeof document === "undefined"
      ) {
        this.hasApp = false;
        return;
      }

      this.hasApp = null;

      let resolved = false;
      let probeFrame = null;

      const markResult = (installed) => {
        if (resolved) {
          return;
        }
        resolved = true;

        // If app is detected as opened, cancel any pending web/store fallback timers
        if (installed && this.fallbackTimer) {
          clearTimeout(this.fallbackTimer);
          this.fallbackTimer = null;
        }

        this.hasApp = installed;
        this.teardownDetection();
      };

      const onVisibilityChange = () => {
        if (document.hidden) {
          markResult(true);
          this.postShortlinkEvent(this.code, "opened_app");
        }
      };

      const onPageHide = () => {
        markResult(true);
        this.postShortlinkEvent(this.code, "opened_app");
      };

      document.addEventListener("visibilitychange", onVisibilityChange);
      window.addEventListener("pagehide", onPageHide, true);

      this.detectionCleanup = () => {
        document.removeEventListener("visibilitychange", onVisibilityChange);
        window.removeEventListener("pagehide", onPageHide, true);
        if (probeFrame && probeFrame.parentNode) {
          probeFrame.parentNode.removeChild(probeFrame);
          probeFrame = null;
        }
      };

      this.detectionTimer = window.setTimeout(() => {
        markResult(false);
      }, 1500);

      this.$nextTick(() => {
        if (!document.body) {
          return;
        }
        if (this.platform === "ios") {
          const anchor = document.createElement("a");
          anchor.style.display = "none";
          anchor.href = this.deepLink;
          document.body.appendChild(anchor);
          anchor.click();
          document.body.removeChild(anchor);
          return;
        }
        probeFrame = document.createElement("iframe");
        probeFrame.style.display = "none";
        probeFrame.src = this.deepLink;
        document.body.appendChild(probeFrame);
      });
    },
    async postShortlinkEvent(code, event, platform) {
      try {
        await ApiService.post(
          `/shortlinks/${encodeURIComponent(code)}/events`,
          {
            event, // 'clicked' | 'opened_app' | 'fallback_web' | 'fallback_store'
            platform: platform || this.detectPlatform(), // 'ios' | 'android' | 'web'
            ua: navigator.userAgent,
          }
        );
      } catch (_) {
        // event fire-and-forget
      }
    },
    handlePrimaryAction() {
      if (!this.isMobile) {
        this.redirectToStore();
        return;
      }

      if (this.hasApp === false) {
        this.redirectToStore();
        return;
      }

      this.openApp(this.hasApp !== true);
    },
    openApp(withFallback) {
      if (!this.deepLink) {
        this.redirectToStore();
        return;
      }

      if (this.platform === "android") {
        // Use intent to prefer app if installed
        const intent = `intent://s/${encodeURIComponent(
          this.code
        )}/?shortLinkCode=${encodeURIComponent(
          this.code
        )}#Intent;scheme=robosay;package=tr.com.robosay;end`;
        window.location.href = intent;
      } else {
        window.location.href = this.deepLink;
      }

      if (withFallback) {
        // Redundant safety: cancel fallback timer as soon as page hides (app switches to foreground)
        const cancelFallback = () => {
          if (this.fallbackTimer) {
            clearTimeout(this.fallbackTimer);
            this.fallbackTimer = null;
          }
          document.removeEventListener("visibilitychange", onVis);
          window.removeEventListener("pagehide", cancelFallback, true);
        };
        const onVis = () => {
          if (typeof document !== "undefined" && document.hidden) {
            cancelFallback();
          }
        };
        document.addEventListener("visibilitychange", onVis);
        window.addEventListener("pagehide", cancelFallback, true);

        // (Re)set fallback timer with a slightly longer grace period for iOS/Safari
        if (this.fallbackTimer) clearTimeout(this.fallbackTimer);
        this.fallbackTimer = window.setTimeout(() => {
          // Do NOT redirect anywhere; stay on this page in "not installed" state
          this.hasApp = false;
          this.postShortlinkEvent(this.code, "not_installed");

          // cleanup listeners after fallback fires
          document.removeEventListener("visibilitychange", onVis);
          window.removeEventListener("pagehide", cancelFallback, true);
        }, 2200);
      }
    },
    redirectToStore() {
      this.postShortlinkEvent(this.code, "fallback_store");
      const url = this.storeLink;
      if (url) {
        const openedWindow = window.open(url, "_blank", "noopener,noreferrer");

        if (!openedWindow) {
          window.location.href = url;
        }
      }
    },
    teardownDetection() {
      if (this.detectionCleanup) {
        this.detectionCleanup();
        this.detectionCleanup = null;
      }
      if (this.detectionTimer) {
        clearTimeout(this.detectionTimer);
        this.detectionTimer = null;
      }
    },
  },
};
</script>
